import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  getMfaStatus,
  enableTotpSetup,
  confirmTotp,
  enableEmailMfa,
  disableMfa,
  formatApiError,
  getTenantSubdomain,
} from '../services/api';
import './Login.css';

const MFASettings = () => {
  const { t } = useTranslation(['profile', 'common']);
  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const [status, setStatus] = useState({ mfa_enabled: false, method: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // TOTP setup flow
  const [totpStep, setTotpStep] = useState('idle'); // 'idle' | 'qr' | 'confirm'
  const [totpData, setTotpData] = useState(null); // { secret, provisioning_uri, qr_code_base64 }
  const [totpCode, setTotpCode] = useState('');
  const [totpSubmitting, setTotpSubmitting] = useState(false);
  const [totpError, setTotpError] = useState(null);

  // Email enable
  const [emailEnabling, setEmailEnabling] = useState(false);

  // Disable MFA
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableSubmitting, setDisableSubmitting] = useState(false);
  const [disableError, setDisableError] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMfaStatus();
      setStatus({ mfa_enabled: data.mfa_enabled, method: data.method });
    } catch (err) {
      const formatted = formatApiError(err);
      setError(formatted.generalError || t('mfaLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStartTotp = async () => {
    setTotpError(null);
    setTotpStep('qr');
    setTotpData(null);
    setTotpCode('');
    try {
      const data = await enableTotpSetup();
      setTotpData(data);
    } catch (err) {
      const formatted = formatApiError(err);
      setTotpError(formatted.generalError || t('setupFailed'));
      setTotpStep('idle');
    }
  };

  const handleConfirmTotp = async (e) => {
    e.preventDefault();
    if (!totpCode.trim() || totpCode.length < 6) {
      setTotpError(t('enterSixDigitError'));
      return;
    }
    setTotpSubmitting(true);
    setTotpError(null);
    try {
      await confirmTotp(totpCode);
      setSuccess(t('authenticatorEnabled'));
      setTotpStep('idle');
      setTotpData(null);
      setTotpCode('');
      fetchStatus();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      const formatted = formatApiError(err);
      setTotpError(formatted.generalError || (formatted.fieldErrors?.code && formatted.fieldErrors.code[0]) || t('invalidCode'));
    } finally {
      setTotpSubmitting(false);
    }
  };

  const handleCancelTotp = () => {
    setTotpStep('idle');
    setTotpData(null);
    setTotpCode('');
    setTotpError(null);
  };

  const handleEnableEmail = async () => {
    setEmailEnabling(true);
    setError(null);
    try {
      await enableEmailMfa();
      setSuccess(t('emailMfaEnabled'));
      fetchStatus();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      const formatted = formatApiError(err);
      setError(formatted.generalError || t('enableEmailFailed'));
    } finally {
      setEmailEnabling(false);
    }
  };

  const openDisableModal = () => {
    setDisablePassword('');
    setDisableError(null);
    setDisableModalOpen(true);
  };

  const closeDisableModal = () => {
    setDisableModalOpen(false);
    setDisablePassword('');
    setDisableError(null);
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    if (!disablePassword.trim()) {
      setDisableError(t('enterPassword'));
      return;
    }
    setDisableSubmitting(true);
    setDisableError(null);
    try {
      await disableMfa(disablePassword);
      setSuccess(t('mfaDisabled'));
      closeDisableModal();
      fetchStatus();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      const formatted = formatApiError(err);
      setDisableError(formatted.generalError || (formatted.fieldErrors?.password && formatted.fieldErrors.password[0]) || t('disableMfaFailed'));
    } finally {
      setDisableSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('mfaTitle')}</h3>
        <p className="text-gray-500 text-sm">{t('common:loading')}</p>
      </div>
    );
  }

  const methodLabel = status.method === 'totp' ? t('methodAuthenticator') : status.method === 'email' ? t('methodEmail') : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('mfaTitle')}</h3>
      <p className="text-gray-500 text-sm mb-4">
        {t('mfaDescription')}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {status.mfa_enabled ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('mfaStatusLabel')} <span className="font-medium text-green-600">{t('mfaStatusOn')}</span>
            {methodLabel && <span className="text-gray-600"> ({methodLabel})</span>}
          </p>
          <button
            type="button"
            onClick={openDisableModal}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">lock_open</span>
            {t('disableMfa')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {totpStep === 'idle' && (
            <>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleStartTotp}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity ${isRsTenant ? 'bg-[#2a2a2a]' : 'bg-[#0045F5]'}`}
                >
                  <span className="material-symbols-outlined text-lg">phonelink_lock</span>
                  {t('enableAuthenticator')}
                </button>
                <button
                  type="button"
                  onClick={handleEnableEmail}
                  disabled={emailEnabling}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 bg-white dark:bg-slate-800 transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">mail</span>
                  {emailEnabling ? t('enabling') : t('enableEmail')}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {t('mfaMethodHint')}
              </p>
            </>
          )}

          {totpStep === 'qr' && totpData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <p className="text-sm font-medium text-gray-700">{t('scanQr')}</p>
              <div className="flex justify-center">
                <img src={totpData.qr_code_base64} alt="QR code" className="w-48 h-48 object-contain border border-gray-200 rounded" />
              </div>
              <p className="text-xs text-gray-500">
                {t('manualSecret')} <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">{totpData.secret}</code>
              </p>
              {totpError && (
                <p className="text-sm text-red-600">{totpError}</p>
              )}
              <form onSubmit={handleConfirmTotp} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('enterSixDigit')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="000000"
                  className={`w-full max-w-[140px] border-2 border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-center font-mono text-lg focus:ring-0 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 ${isRsTenant ? 'focus:border-[#2a2a2a]' : 'focus:border-[#0045F5]'}`}
                  disabled={totpSubmitting}
                />
                <div className="flex gap-2">
                  <button type="button" onClick={handleCancelTotp} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                    {t('common:cancel')}
                  </button>
                  <button type="submit" disabled={totpSubmitting || totpCode.length < 6} className={`px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 ${isRsTenant ? 'bg-[#2a2a2a]' : 'bg-[#0045F5]'}`}>
                    {totpSubmitting ? t('verifying') : t('common:confirm')}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      )}

      {/* Disable MFA modal */}
      <AnimatePresence>
        {disableModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={(e) => e.target === e.currentTarget && closeDisableModal()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('disableModalTitle')}</h3>
              <p className="text-sm text-gray-600 mb-4">{t('disableModalBody')}</p>
              {disableError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{disableError}</p>
                </div>
              )}
              <form onSubmit={handleDisableMfa}>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder={t('yourPassword')}
                  className={`w-full border-2 border-gray-300 dark:border-slate-600 rounded-lg px-4 py-2 mb-4 focus:ring-0 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 ${isRsTenant ? 'focus:border-[#2a2a2a]' : 'focus:border-[#0045F5]'}`}
                  disabled={disableSubmitting}
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={closeDisableModal} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                    {t('common:cancel')}
                  </button>
                  <button type="submit" disabled={disableSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                    {disableSubmitting ? t('disabling') : t('disable2fa')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MFASettings;
